import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Container,
  HeaderBar,
  DataGridTable,
  Blade,
  SubmitDialog,
  Toast as toast,
  SliderWithInput as Slider,
  TypoGraph,
} from 'components'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import styles from './rooExportSettings.module.scss'
import {
  getRooExportSettingsColumns,
  actionList,
} from './rooExportSettingsTableData'
import RooExportTableToolbar from './RooExportTableToolbar'
import strings from 'strings'
import {
  getAllExportSettings,
  updateExportSettings,
  deleteExportSettings,
  getMyCarrierList,
  updateCompanyRadiusDefaults,
} from 'api'
import { getCarrierTableData } from 'tools/tableDataHelper'
import { isViewOnly } from 'tools/auth'
import { reportException } from 'tools/logs'
import { isShortcodeEnabledForNBFleetDashboard } from 'tools/helper'
import { dispatchCompanyRadiusDefaults } from 'actions/companyConfig'
import { updateStoredCompanyRadiusDefaults } from 'tools/auth'

const t = strings.ROO_EXPORT_SETTINGS
const useKilometers = isShortcodeEnabledForNBFleetDashboard()
const distanceUnit = useKilometers ? 'Km' : 'Miles'

// Module-level carrier cache — survives re-renders, cleared on navigation away
const CARRIER_CACHE_TTL_MS = 10 * 60 * 1000 // 10 minutes
let carrierCache: { data: any[]; ts: number } | null = null

function getCachedCarriers(): any[] | null {
  if (!carrierCache) return null
  if (Date.now() - carrierCache.ts > CARRIER_CACHE_TTL_MS) {
    carrierCache = null
    return null
  }
  return carrierCache.data
}

function setCachedCarriers(data: any[]): void {
  carrierCache = { data, ts: Date.now() }
}

const GLOBAL_MIN = 0
const GLOBAL_MAX = useKilometers ? 4000 : 2500
const GAP = 0.1
const KM_FACTOR = 1.60934
const HARDCODED_DEFAULTS_MILES = [100, 250, 500, 1000]

function roundToDecimalPlaces(n: number, decimalPlaces: number): number {
  const f = 10 ** decimalPlaces
  return Math.round(n * f) / f
}

const round1 = (n: number) => roundToDecimalPlaces(n, 1)

function cascade(values: number[], changedIdx: number): number[] {
  const r = [...values]
  for (let i = changedIdx + 1; i < r.length; i++) {
    if (r[i] <= r[i - 1]) r[i] = round1(r[i - 1] + GAP)
  }
  if (r[r.length - 1] > GLOBAL_MAX) {
    r[r.length - 1] = GLOBAL_MAX
    for (let i = r.length - 2; i >= 0; i--) {
      if (r[i] >= r[i + 1]) r[i] = round1(r[i + 1] - GAP)
    }
  }
  for (let i = changedIdx - 1; i >= 0; i--) {
    if (r[i] >= r[i + 1]) r[i] = round1(r[i + 1] - GAP)
  }
  if (r[0] < GLOBAL_MIN) {
    r[0] = GLOBAL_MIN
    for (let i = 1; i < r.length; i++) {
      if (r[i] <= r[i - 1]) r[i] = round1(r[i - 1] + GAP)
    }
  }
  return r
}

const sliderTitles = ['Radius 1', 'Radius 2', 'Radius 3', 'Radius 4']

const carrierColumns = [
  {
    field: 'carrierId',
    headerName: 'ID',
    minWidth: 80,
    flex: 0,
    sortable: false,
  },
  {
    field: 'carrierName',
    headerName: 'Carrier Name',
    minWidth: 160,
    flex: 1.5,
    sortable: true,
  },
  { field: 'dot', headerName: 'DOT', minWidth: 100, flex: 0.8, sortable: true },
  {
    field: 'cuid',
    headerName: 'CUID',
    minWidth: 120,
    flex: 0.8,
    sortable: true,
  },
  {
    field: 'policyId',
    headerName: 'Policy ID',
    minWidth: 120,
    flex: 0.8,
    sortable: true,
  },
]

const RooExportSettingsPage: React.FC = () => {
  const theme = useTheme()
  const isMdScreen = useMediaQuery(theme.breakpoints.between('sm', 'lg'))
  const bladeSize: 'sm' | 'md' | 'lg' = isMdScreen ? 'md' : 'lg'
  const dispatch = useDispatch()

  const storedCompanyDefaults = useSelector(
    (state: any) => state?.companyConfig?.companyRadiusDefaults
  )
  const defaultRadii = storedCompanyDefaults
    ? [
        storedCompanyDefaults.radius1,
        storedCompanyDefaults.radius2,
        storedCompanyDefaults.radius3,
        storedCompanyDefaults.radius4,
      ].map((r) => round1(useKilometers ? r * KM_FACTOR : r))
    : HARDCODED_DEFAULTS_MILES.map((r) =>
        useKilometers ? round1(r * KM_FACTOR) : r
      )

  const [loading, setLoading] = useState(false)
  const [btnLoader, setBtnLoader] = useState(false)
  const [exportSettingsList, setExportSettingsList] = useState<any[]>([])

  // Company defaults dialog state
  const [companyDefaultsDialogOpen, setCompanyDefaultsDialogOpen] =
    useState(false)
  const [companyDefaultsRadii, setCompanyDefaultsRadii] =
    useState<number[]>(defaultRadii)
  const [companyDefaultsSnapKeys, setCompanyDefaultsSnapKeys] = useState([
    0, 0, 0, 0,
  ])
  const [companyDefaultsBtnLoader, setCompanyDefaultsBtnLoader] =
    useState(false)

  // Blade state
  const [bladeOpen, setBladeOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<any>(null)

  // Carrier list (add mode)
  const [allCarriers, setAllCarriers] = useState<any[]>([])
  const [carriersLoading, setCarriersLoading] = useState(false)
  const selectedCarrierIdsRef = useRef<GridRowSelectionModel>([])
  const [selectionDisabled, setSelectionDisabled] = useState(true)

  // Radius dialog
  const [radiusDialogOpen, setRadiusDialogOpen] = useState(false)
  const [dialogCarrierNames, setDialogCarrierNames] = useState<string[]>([])
  const [dialogCarrierInfo, setDialogCarrierInfo] = useState<any>(null)

  // Sliders
  const [radii, setRadii] = useState<number[]>(defaultRadii)
  const [sliderSnapKeys, setSliderSnapKeys] = useState([0, 0, 0, 0])

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [pendingDeleteCarrierIds, setPendingDeleteCarrierIds] = useState<
    (number | string)[]
  >([])

  // Main table bulk selection
  const [mainTableSelection, setMainTableSelection] =
    useState<GridRowSelectionModel>([])
  const mainTableSelectedRows = useMemo(
    () => exportSettingsList.filter((e) => mainTableSelection.includes(e.id)),
    [exportSettingsList, mainTableSelection]
  )

  // Carrier IDs pending the radius dialog (used by both add flow and bulk edit from main table)
  const [pendingRadiusCarrierIds, setPendingRadiusCarrierIds] = useState<
    (number | string)[]
  >([])

  const fetchAllCarriers = useCallback((showSpinner = true) => {
    const cached = getCachedCarriers()
    if (cached) {
      setAllCarriers(cached)
      return
    }
    if (showSpinner) setCarriersLoading(true)
    getMyCarrierList()
      .then((res: any) => {
        const rawData = getCarrierTableData(res) ?? []
        // Deduplicate: radius config is per carrier, not per ELD provider
        const seen = new Set()
        const unique = rawData.filter((row: any) => {
          if (seen.has(row.carrierId)) return false
          seen.add(row.carrierId)
          return true
        })
        setCachedCarriers(unique)
        setAllCarriers(unique)
      })
      .catch((error: Error) => reportException(error))
      .finally(() => setCarriersLoading(false))
  }, [])

  const fetchExportSettings = useCallback(() => {
    setLoading(true)
    getAllExportSettings(useKilometers)
      .then((response: any) => {
        setExportSettingsList(response?.data ?? response ?? [])
      })
      .catch((error: Error) => reportException(error))
      .finally(() => setLoading(false))
  }, [])

  // Exclude carriers that already have export settings configured
  const selectableCarriers = useMemo(
    () =>
      allCarriers.filter(
        (c) => !exportSettingsList.some((e) => e.carrierId === c.carrierId)
      ),
    [allCarriers, exportSettingsList]
  )

  // Keep company defaults sliders in sync when Redux state changes (e.g. after a save)
  useEffect(() => {
    setCompanyDefaultsRadii(defaultRadii)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedCompanyDefaults])

  const handleCompanyDefaultsChange = useCallback(
    (index: number, value: number) => {
      const current = [...companyDefaultsRadii]
      current[index] = value
      const newRadii = cascade(current, index)
      if (newRadii.every((v, i) => v === companyDefaultsRadii[i])) {
        setCompanyDefaultsSnapKeys((keys) =>
          keys.map((k, ki) => (ki === index ? k + 1 : k))
        )
        return
      }
      setCompanyDefaultsRadii(newRadii)
    },
    [companyDefaultsRadii]
  )

  const handleCloseCompanyDefaultsDialog = useCallback(() => {
    if (companyDefaultsBtnLoader) return
    setCompanyDefaultsDialogOpen(false)
  }, [companyDefaultsBtnLoader])

  const handleCompanyDefaultsDialogExited = useCallback(() => {
    setCompanyDefaultsRadii(defaultRadii)
    setCompanyDefaultsSnapKeys((keys) => keys.map((k) => k + 1))
  }, [defaultRadii])

  const resetCompanyDefaults = useCallback(() => {
    const hardcodedDisplay = HARDCODED_DEFAULTS_MILES.map((r) =>
      useKilometers ? round1(r * KM_FACTOR) : r
    )
    setCompanyDefaultsRadii(hardcodedDisplay)
    setCompanyDefaultsSnapKeys((keys) => keys.map((k) => k + 1))
  }, [])

  const saveCompanyDefaults = useCallback(() => {
    setCompanyDefaultsBtnLoader(true)
    // Send display values — backend converts km→miles via useKilometers flag (same as per-carrier endpoint)
    const displayPayload = {
      radius1: companyDefaultsRadii[0],
      radius2: companyDefaultsRadii[1],
      radius3: companyDefaultsRadii[2],
      radius4: companyDefaultsRadii[3],
    }
    // Redux/localStorage always store miles
    const milesPayload = {
      radius1: useKilometers
        ? companyDefaultsRadii[0] / KM_FACTOR
        : companyDefaultsRadii[0],
      radius2: useKilometers
        ? companyDefaultsRadii[1] / KM_FACTOR
        : companyDefaultsRadii[1],
      radius3: useKilometers
        ? companyDefaultsRadii[2] / KM_FACTOR
        : companyDefaultsRadii[2],
      radius4: useKilometers
        ? companyDefaultsRadii[3] / KM_FACTOR
        : companyDefaultsRadii[3],
    }
    updateCompanyRadiusDefaults(displayPayload, useKilometers)
      .then((response: any) => {
        toast({
          id: 'companyDefaultsSave',
          type: 'success',
          subTitle: response?.message,
        })
        dispatch(dispatchCompanyRadiusDefaults(milesPayload))
        updateStoredCompanyRadiusDefaults(milesPayload)
        setCompanyDefaultsDialogOpen(false)
      })
      .catch((error: Error) => reportException(error))
      .finally(() => setCompanyDefaultsBtnLoader(false))
  }, [companyDefaultsRadii, dispatch, useKilometers])

  // Both fetches fire in parallel on mount — export settings for the main table,
  // carriers silently pre-fetched so the blade opens instantly
  useEffect(() => {
    fetchExportSettings()
    fetchAllCarriers(false)
  }, [fetchExportSettings, fetchAllCarriers])

  const handleRadiusChange = useCallback(
    (index: number, value: number) => {
      const current = [...radii]
      current[index] = value
      const newRadii = cascade(current, index)
      if (newRadii.every((v, i) => v === radii[i])) {
        setSliderSnapKeys((keys) =>
          keys.map((k, ki) => (ki === index ? k + 1 : k))
        )
        return
      }
      setRadii(newRadii)
    },
    [radii]
  )

  const openAddBlade = () => {
    setSelectedRow(null)
    selectedCarrierIdsRef.current = []
    setSelectionDisabled(true)
    // Cache miss (TTL expired or first visit after hard refresh) — fetch with spinner
    if (!getCachedCarriers()) fetchAllCarriers()
    setBladeOpen(true)
  }

  const openEditBlade = (row: any) => {
    setSelectedRow(row)
    openRadiusDialog(
      [row.carrierId],
      [row.carrierName ?? String(row.carrierId)],
      [
        round1(row.radius1 ?? defaultRadii[0]),
        round1(row.radius2 ?? defaultRadii[1]),
        round1(row.radius3 ?? defaultRadii[2]),
        round1(row.radius4 ?? defaultRadii[3]),
      ],
      row
    )
  }

  const handleCloseBlade = () => {
    setBladeOpen(false)
    selectedCarrierIdsRef.current = []
    setSelectionDisabled(true)
  }

  const handleCloseRadiusDialog = () => {
    setRadiusDialogOpen(false)
  }

  const handleRadiusDialogExited = () => {
    setDialogCarrierInfo(null)
  }

  const handleActions = (_id: number | string, actionName: string) => {
    const row = exportSettingsList.find((item) => item.id === _id)
    if (!row) return
    setSelectedRow(row)
    if (actionName === 'Edit') {
      openEditBlade(row)
    } else if (actionName === 'Remove') {
      setPendingDeleteCarrierIds([row.carrierId])
      setShowDeleteModal(true)
    }
  }

  const handleCarrierSelection = useCallback(
    (selection: GridRowSelectionModel) => {
      selectedCarrierIdsRef.current = selection
      setSelectionDisabled(selection.length === 0)
    },
    []
  )

  const openRadiusDialog = (
    carrierIds: (number | string)[],
    carrierNames: string[],
    initialRadii?: number[],
    carrierInfo?: any
  ) => {
    setPendingRadiusCarrierIds(carrierIds)
    setDialogCarrierNames(carrierNames)
    setRadii(initialRadii ?? defaultRadii)
    setSliderSnapKeys([0, 0, 0, 0])
    setDialogCarrierInfo(carrierInfo ?? null)
    setRadiusDialogOpen(true)
  }

  const handleConfigureRadii = () => {
    if (selectedCarrierIdsRef.current.length === 0) return
    const ids = selectedCarrierIdsRef.current as (number | string)[]
    const names = ids.map((id) => {
      const carrier = allCarriers.find((c: any) => c.carrierId === id)
      return carrier?.carrierName ?? String(id)
    })
    openRadiusDialog(ids, names)
  }

  const handleBulkConfigureRadii = () => {
    const ids = mainTableSelectedRows.map((r) => r.carrierId)
    const names = mainTableSelectedRows.map(
      (r) => r.carrierName ?? String(r.carrierId)
    )
    const carrierInfo = ids.length === 1 ? mainTableSelectedRows[0] : null
    openRadiusDialog(ids, names, undefined, carrierInfo)
  }

  const handleBulkDelete = () => {
    setPendingDeleteCarrierIds(mainTableSelectedRows.map((r) => r.carrierId))
    setShowDeleteModal(true)
  }

  const saveExportSettings = (carrierIds: (number | string)[]) => {
    setBtnLoader(true)
    updateExportSettings(
      carrierIds,
      round1(radii[0]),
      round1(radii[1]),
      round1(radii[2]),
      round1(radii[3]),
      useKilometers
    )
      .then((response: any) => {
        toast({
          id: 'rooExportSave',
          type: 'success',
          subTitle: response?.message,
        })
        setMainTableSelection([])
        handleCloseRadiusDialog()
        handleCloseBlade()
        fetchExportSettings()
      })
      .catch((error: Error) => reportException(error))
      .finally(() => setBtnLoader(false))
  }

  const handleSaveRadiusDialog = () => {
    if (pendingRadiusCarrierIds.length === 0) return
    saveExportSettings(pendingRadiusCarrierIds)
  }

  const handleDelete = () => {
    if (pendingDeleteCarrierIds.length === 0) return
    setBtnLoader(true)
    deleteExportSettings(pendingDeleteCarrierIds)
      .then((response: any) => {
        toast({
          id: 'rooExportDelete',
          type: 'success',
          subTitle: response?.message,
        })
        fetchExportSettings()
      })
      .catch((error: Error) => reportException(error))
      .finally(() => {
        setBtnLoader(false)
        setShowDeleteModal(false)
        setSelectedRow(null)
        setPendingDeleteCarrierIds([])
        setMainTableSelection([])
      })
  }

  interface RadiusSlidersProps {
    radii: number[]
    snapKeys: number[]
    keyPrefix: string
    onValueChange: (index: number, value: number) => void
  }

  const RadiusSliders = ({
    radii,
    snapKeys,
    keyPrefix,
    onValueChange,
  }: RadiusSlidersProps) => (
    <Container sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <TypoGraph
        className={styles.unitLabel}
        align="center"
        paragraph={false}
        gutterBottom={false}
      >
        {t.ALL_VALUES_IN} {distanceUnit}
      </TypoGraph>
      {radii.map((value, i) => (
        <Slider
          key={`${keyPrefix}-${i}-${snapKeys[i]}`}
          initialValue={value}
          min={GLOBAL_MIN}
          max={GLOBAL_MAX}
          maxInputLength={5}
          step={0.1}
          color="primary"
          useInputLabel={true}
          title={sliderTitles[i]}
          label={distanceUnit}
          onValueChange={(v: number) => onValueChange(i, v)}
        />
      ))}
    </Container>
  )

  return (
    <>
      <HeaderBar
        title={t.PAGE_TITLE}
        {...(!isViewOnly() && {
          secondaryBtnProps: {
            id: 'configureCompanyDefaults',
            'data-testid': 'company-defaults-btn',
            label: t.COMPANY_DEFAULTS_BTN,
            onClick: () => setCompanyDefaultsDialogOpen(true),
          },
          primaryBtnProps: {
            id: 'addRooExportConfig',
            'data-testid': 'configure-carriers-btn',
            label: t.CONFIGURE_CARRIERS,
            onClick: openAddBlade,
          },
        })}
      />
      <Container className={styles.container}>
        <DataGridTable
          title={`${t.TABLE_TITLE} — ${distanceUnit}`}
          columns={getRooExportSettingsColumns(useKilometers)}
          columnVisibilityModel={{ id: false, carrierId: false }}
          data={exportSettingsList}
          loading={loading}
          totalCount={exportSettingsList.length}
          multiSearchFilter={false}
          getRowId={(row: any) => row?.id}
          actionList={isViewOnly() ? [] : actionList}
          handleActions={handleActions}
          checkboxSelection={!isViewOnly()}
          handleCheckboxSelection={(sel: GridRowSelectionModel) =>
            setMainTableSelection(sel)
          }
          customSelectionModel={mainTableSelection}
          sortOrder="asc"
          sortField="carrierName"
          reduceHeight={260}
          showFilter={true}
          {...(!isViewOnly() && {
            components: {
              toolbar: () => (
                <RooExportTableToolbar
                  selectionCount={mainTableSelection.length}
                  onConfigureRadii={handleBulkConfigureRadii}
                  onRemove={handleBulkDelete}
                />
              ),
            },
          })}
        />
      </Container>

      {/* Step 1 — Select carriers */}
      <Blade
        open={bladeOpen}
        title={t.CONFIGURE_CARRIERS}
        onClose={handleCloseBlade}
        bodyNode={
          <Container className={styles.bladeBody}>
            <Container className={styles.carrierTableSection}>
              <DataGridTable
                data={selectableCarriers}
                columns={carrierColumns}
                columnVisibilityModel={{ carrierId: false }}
                getRowId={(row: any) => row?.carrierId}
                totalCount={selectableCarriers.length}
                loading={carriersLoading || loading}
                showFilter={true}
                multiSearchFilter={false}
                showTitle={false}
                checkboxSelection={true}
                handleCheckboxSelection={handleCarrierSelection}
                customSelectionModel={selectedCarrierIdsRef.current}
                reduceHeight={220}
              />
            </Container>
          </Container>
        }
        size={bladeSize}
        footerBtns={{
          primary: {
            id: 'saveRooExportSettings',
            label: t.CONFIGURE_RADIUS,
            onClick: handleConfigureRadii,
            disabled: selectionDisabled,
          },
          secondary: {
            id: 'cancelRooExportSettings',
            label: t.CANCEL,
            variant: 'outlined',
            onClick: handleCloseBlade,
          },
        }}
      />

      {/* Configure / edit radius */}
      <Dialog
        open={radiusDialogOpen}
        onClose={handleCloseRadiusDialog}
        TransitionProps={{ onExited: handleRadiusDialogExited }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            mx: { xs: 1, sm: 3 },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialogCarrierInfo
            ? t.EDIT_EXPORT_SETTINGS
            : dialogCarrierNames.length === 1
            ? `${t.CONFIGURE_RADIUS} — ${dialogCarrierNames[0]}`
            : `${t.CONFIGURE_RADIUS} (${dialogCarrierNames.length})`}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {dialogCarrierInfo && (
            <Container
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                mb: 2,
                pb: 2,
                borderBottom: '1px solid rgba(0,0,0,0.12)',
              }}
            >
              <TypoGraph
                sx={{ fontWeight: 700 }}
                paragraph={false}
                gutterBottom={false}
              >
                {dialogCarrierInfo.carrierName}
              </TypoGraph>
              <Container
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}
              >
                {dialogCarrierInfo.dotNumber && (
                  <TypoGraph
                    className={styles.carrierIdentifier}
                    paragraph={false}
                    gutterBottom={false}
                  >
                    {t.DOT_LABEL}: {dialogCarrierInfo.dotNumber}
                  </TypoGraph>
                )}
                {dialogCarrierInfo.cuid && (
                  <TypoGraph
                    className={styles.carrierIdentifier}
                    paragraph={false}
                    gutterBottom={false}
                  >
                    {dialogCarrierInfo.cuidName ?? t.CUID_LABEL}:{' '}
                    {dialogCarrierInfo.cuid}
                  </TypoGraph>
                )}
                {dialogCarrierInfo.policyId && (
                  <TypoGraph
                    className={styles.carrierIdentifier}
                    paragraph={false}
                    gutterBottom={false}
                  >
                    {t.POLICY_ID_LABEL}: {dialogCarrierInfo.policyId}
                  </TypoGraph>
                )}
              </Container>
            </Container>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mb: 2.5,
              px: 1.5,
              py: 1.25,
              borderRadius: 1.5,
              bgcolor: 'rgba(25, 118, 210, 0.06)',
              border: '1px solid rgba(25, 118, 210, 0.18)',
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 17,
                color: 'primary.main',
                mt: 0.25,
                flexShrink: 0,
              }}
            />
            <TypoGraph
              variant="body2"
              sx={{ color: 'text.secondary', m: 0 }}
              paragraph={false}
              gutterBottom={false}
            >
              {dialogCarrierNames.length > 1
                ? t.CARRIER_EXPORT_DESCRIPTION_PLURAL
                : t.CARRIER_EXPORT_DESCRIPTION}
            </TypoGraph>
          </Box>
          <RadiusSliders
            radii={radii}
            snapKeys={sliderSnapKeys}
            keyPrefix="roo-export"
            onValueChange={handleRadiusChange}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleCloseRadiusDialog}
            disabled={btnLoader}
            sx={{ borderRadius: '20px', px: 3 }}
          >
            {t.CANCEL}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveRadiusDialog}
            disabled={btnLoader}
            startIcon={btnLoader ? <CircularProgress size={16} /> : undefined}
            sx={{ borderRadius: '20px', px: 3 }}
          >
            {t.SAVE}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Company defaults dialog */}
      <Dialog
        open={companyDefaultsDialogOpen}
        onClose={handleCloseCompanyDefaultsDialog}
        TransitionProps={{ onExited: handleCompanyDefaultsDialogExited }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            mx: { xs: 1, sm: 3 },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t.COMPANY_DEFAULTS_TITLE}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mb: 2.5,
              px: 1.5,
              py: 1.25,
              borderRadius: 1.5,
              bgcolor: 'rgba(25, 118, 210, 0.06)',
              border: '1px solid rgba(25, 118, 210, 0.18)',
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 17,
                color: 'primary.main',
                mt: 0.25,
                flexShrink: 0,
              }}
            />
            <Box>
              <TypoGraph
                variant="body2"
                sx={{ color: 'text.primary', fontWeight: 500, m: 0 }}
                paragraph={false}
                gutterBottom={false}
              >
                {t.COMPANY_DEFAULTS_DESCRIPTION_LINE1}
              </TypoGraph>
              <TypoGraph
                variant="body2"
                sx={{ color: 'text.secondary', m: 0 }}
                paragraph={false}
                gutterBottom={false}
              >
                {t.COMPANY_DEFAULTS_DESCRIPTION_LINE2}
              </TypoGraph>
            </Box>
          </Box>
          <RadiusSliders
            radii={companyDefaultsRadii}
            snapKeys={companyDefaultsSnapKeys}
            keyPrefix="roo-defaults"
            onValueChange={handleCompanyDefaultsChange}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={resetCompanyDefaults}
            disabled={companyDefaultsBtnLoader}
            sx={{ borderRadius: '20px', px: 3, mr: 'auto' }}
          >
            {t.RESET_TO_DEFAULTS}
          </Button>
          <Button
            variant="outlined"
            onClick={handleCloseCompanyDefaultsDialog}
            disabled={companyDefaultsBtnLoader}
            sx={{ borderRadius: '20px', px: 3 }}
          >
            {t.CANCEL}
          </Button>
          <Button
            variant="contained"
            onClick={saveCompanyDefaults}
            disabled={companyDefaultsBtnLoader}
            startIcon={
              companyDefaultsBtnLoader ? (
                <CircularProgress size={16} />
              ) : undefined
            }
            sx={{ borderRadius: '20px', px: 3 }}
          >
            {t.SAVE}
          </Button>
        </DialogActions>
      </Dialog>

      <SubmitDialog
        cancelId="cancelRemoveRooExport"
        type="delete"
        maxWidth="sm"
        open={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        title={
          pendingDeleteCarrierIds.length > 1
            ? t.REMOVE_BULK_CONFIRM_TITLE
            : t.REMOVE_CONFIRM_TITLE
        }
        body1={
          pendingDeleteCarrierIds.length > 1
            ? t.REMOVE_BULK_CONFIRM_BODY.replace(
                '{0}',
                String(pendingDeleteCarrierIds.length)
              )
            : selectedRow?.carrierName
            ? t.REMOVE_CONFIRM_BODY.replace('{0}', selectedRow.carrierName)
            : ''
        }
        body2={t.REMOVE_CONFIRM_WARNING}
        primaryBtnProps={{
          id: 'confirmRemoveRooExport',
          label: t.CONFIRM_REMOVE,
          loading: btnLoader,
          onClick: handleDelete,
          disabled: btnLoader,
        }}
      />
    </>
  )
}

export default RooExportSettingsPage
